import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Check, ArrowRight, Mail, Lock, User, Building2, Zap, Clock, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const SignupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    phoneNumber: ''
  });

  const trialBenefits = [
    { icon: Clock, text: '14 days completely free', subtext: 'No credit card required' },
    { icon: Zap, text: 'Full feature access', subtext: 'Try everything, no limitations' },
    { icon: Shield, text: 'Cancel anytime', subtext: 'No questions asked' },
    { icon: CreditCard, text: 'No payment info needed', subtext: 'Start immediately' }
  ];

  const features = [
    'Monitor unlimited endpoints',
    'Advanced automation tools',
    'Integrated help desk & PSA',
    'Real-time performance tracking',
    '24/7 support during trial',
    'Mobile app access',
    'API access included',
    'Custom branding options'
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    // TODO: Call actual signup API
    toast.success('Account created! Redirecting...');
    
    // Redirect to dashboard after short delay
    setTimeout(() => {
      navigate('/dashboard');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-pink-500 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTMwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHptMzAgMzBjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTMwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHpNNiAzNGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6bTAtMzBjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>

      <div className="w-full max-w-6xl relative z-10">
        {/* Logo/Back Button */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-white hover:text-blue-100 transition-colors"
          >
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-xl font-bold hidden sm:inline">RMM Pro</span>
          </button>
          <button 
            onClick={() => navigate('/login')}
            className="text-white hover:text-blue-100 transition-colors text-sm"
          >
            Already have an account? <span className="font-semibold underline">Log in</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left Side - Benefits */}
          <div className="text-white space-y-8 order-2 lg:order-1">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Start Your Free Trial Today
              </h1>
              <p className="text-xl text-blue-100">
                Join 500+ MSPs managing over 10,000 endpoints with RMM Pro
              </p>
            </div>

            {/* Trial Benefits Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {trialBenefits.map((benefit, index) => (
                <Card key={index} className="bg-white/10 backdrop-blur-lg border-white/20">
                  <CardContent className="p-4">
                    <benefit.icon className="w-8 h-8 text-white mb-2" />
                    <div className="font-semibold text-white mb-1">{benefit.text}</div>
                    <div className="text-sm text-blue-100">{benefit.subtext}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Features List */}
            <div>
              <h3 className="text-xl font-semibold mb-4">What's included in your trial:</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-300 flex-shrink-0" />
                    <span className="text-blue-50">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Social Proof */}
            <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 border-2 border-white flex items-center justify-center text-white font-semibold">
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="font-semibold text-white">Join 500+ MSPs</div>
                  <div className="text-sm text-blue-100">Already growing with RMM Pro</div>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-300 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                  </svg>
                ))}
                <span className="ml-2 text-white font-semibold">4.9/5</span>
                <span className="text-blue-100 text-sm ml-1">(200+ reviews)</span>
              </div>
            </div>
          </div>

          {/* Right Side - Signup Form */}
          <div className="order-1 lg:order-2">
            <Card className="shadow-2xl">
              <CardContent className="p-8">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Create Your Account</h2>
                  <p className="text-slate-600">Get started in less than 2 minutes</p>
                </div>

                {/* Progress Indicator */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-400'} font-semibold`}>
                      1
                    </div>
                    <span className="text-sm font-medium text-slate-700">Your Info</span>
                  </div>
                  <div className="flex-1 h-1 bg-slate-200 mx-2">
                    <div className={`h-full bg-blue-600 transition-all duration-300`} style={{width: step >= 2 ? '100%' : '0%'}}></div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-400'} font-semibold`}>
                      2
                    </div>
                    <span className="text-sm font-medium text-slate-700">Company</span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {step === 1 && (
                    <>
                      <div>
                        <Label htmlFor="fullName" className="text-slate-700">Full Name</Label>
                        <div className="relative mt-1">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input
                            id="fullName"
                            name="fullName"
                            type="text"
                            placeholder="John Doe"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            required
                            className="pl-10 h-12 border-slate-300"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="john@company.com"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="pl-10 h-12 border-slate-300"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="password" className="text-slate-700">Password</Label>
                        <div className="relative mt-1">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            placeholder="Min. 8 characters"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            className="pl-10 h-12 border-slate-300"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                        <div className="relative mt-1">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            placeholder="Re-enter password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            required
                            className="pl-10 h-12 border-slate-300"
                          />
                        </div>
                      </div>

                      <Button
                        type="button"
                        onClick={() => {
                          if (formData.fullName && formData.email && formData.password && formData.confirmPassword) {
                            if (formData.password === formData.confirmPassword) {
                              setStep(2);
                            } else {
                              toast.error('Passwords do not match');
                            }
                          } else {
                            toast.error('Please fill in all fields');
                          }
                        }}
                        className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg"
                      >
                        Continue
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <div>
                        <Label htmlFor="companyName" className="text-slate-700">Company Name</Label>
                        <div className="relative mt-1">
                          <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input
                            id="companyName"
                            name="companyName"
                            type="text"
                            placeholder="Your MSP Company"
                            value={formData.companyName}
                            onChange={handleInputChange}
                            required
                            className="pl-10 h-12 border-slate-300"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="phoneNumber" className="text-slate-700">Phone Number (Optional)</Label>
                        <div className="relative mt-1">
                          <Input
                            id="phoneNumber"
                            name="phoneNumber"
                            type="tel"
                            placeholder="+1 (555) 123-4567"
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                            className="h-12 border-slate-300"
                          />
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-slate-700">
                            <strong>Your data is secure.</strong> We use enterprise-grade encryption and never share your information.
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-3">
                        <Button
                          type="button"
                          onClick={() => setStep(1)}
                          variant="outline"
                          className="flex-1 h-12"
                        >
                          Back
                        </Button>
                        <Button
                          type="submit"
                          className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        >
                          Start Free Trial
                        </Button>
                      </div>
                    </>
                  )}

                  <div className="text-center text-xs text-slate-500 mt-4">
                    By signing up, you agree to our{' '}
                    <a href="#" className="text-blue-600 hover:underline">Terms of Service</a>
                    {' '}and{' '}
                    <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Trust Badges */}
            <div className="mt-6 flex items-center justify-center space-x-6 text-white">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <span className="text-sm">SSL Secure</span>
              </div>
              <div className="flex items-center space-x-2">
                <Lock className="w-5 h-5" />
                <span className="text-sm">GDPR Compliant</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="w-5 h-5" />
                <span className="text-sm">SOC 2 Certified</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
